import React, { FC, ReactElement } from "react";
import { Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";

import { NavMenuChild } from "./navbarConfig";
import classes from "./Navbar.module.css";

interface NavItemProps {
  icon: ReactElement;
  label: string;
  link: string;
  children: NavMenuChild[];
  isNavExpanded: boolean;
  isAccordionOpen: boolean;
  showActive: boolean;
  currentPath: string;
  onSelect: () => void;
  onChildNavigate: (link: string) => void;
}

const NavItem: FC<NavItemProps> = ({
  icon, label, children,
  isNavExpanded, isAccordionOpen, showActive, currentPath,
  onSelect, onChildNavigate,
}) => {
  const clonedIcon = React.cloneElement(icon, {
    ...icon.props,
    size: 20,
    stroke: showActive ? 2 : 1.5,
    color: "white",
  });

  const button = (
    <UnstyledButton
      className={classes.navItem}
      data-active={showActive || undefined}
      w="100%"
      onClick={(!isNavExpanded && children.length > 0) ? undefined : onSelect}
      aria-label={!isNavExpanded ? label : undefined}
    >
      <div className={classes.linkIconBox}>
        {clonedIcon}
      </div>
      <span className={`${classes.linkLabel}${!isNavExpanded ? ` ${classes.linkLabelHidden}` : ""}`}>
        {label}
      </span>
      {isNavExpanded && children.length > 0 && (
        isAccordionOpen
          ? <IconChevronDown size={14} color="rgba(255,255,255,0.5)" />
          : <IconChevronRight size={14} color="rgba(255,255,255,0.5)" />
      )}
    </UnstyledButton>
  );

  // Collapsed + has children → Menu hover
  if (!isNavExpanded && children.length > 0) {
    return (
      <div style={{ width: "100%" }}>
        <Menu position="right-start" offset={12} shadow="md" withinPortal trigger="hover" openDelay={100} closeDelay={150}>
          <Menu.Target>
            <Tooltip label={label} position="right" transitionProps={{ duration: 0 }}>
              {button}
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            {children.map((child, i) => (
              <Menu.Item
                key={i}
                onClick={() => onChildNavigate(child.link)}
                data-active={currentPath === child.link || undefined}
              >
                {child.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </div>
    );
  }

  // Collapsed + no children → Tooltip only
  if (!isNavExpanded) {
    return (
      <Tooltip label={label} position="right" transitionProps={{ duration: 0 }}>
        {button}
      </Tooltip>
    );
  }

  // Expanded → accordion
  return (
    <div style={{ width: "100%" }}>
      {button}
      {isAccordionOpen && (
        <div className={classes.subLinkGroup}>
          {children.map((child) => (
            <a
              key={child.key}
              href={child.link}
              className={classes.subLink}
              data-active={currentPath === child.link || undefined}
              onClick={(e) => {
                e.preventDefault();
                onChildNavigate(child.link);
              }}
            >
              {child.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default NavItem;
